import { useCallback, useEffect, useRef } from 'react';
import { ADDRESS_SOURCE_LABEL_KEYS, ADDRESS_SOURCE_NAME_KEYS, Color, RETRY_AFTER_MS, Size, clockInZone, failedAnswer, fill, noteLine, noteSentence, t, useVerifyStep, verifiedByUserPath, verifyLocationPath, type Address, type AddressAgreement, type AddressCandidate, type AddressCorrection, type LocationVerifyAnswer, type VerifyChoice, postalAddressLine } from '@bms2/shared';
import { Button } from '../../components/controls';
import { useViewerZone } from '../../shell/ViewerZone';
import { PAGES_PATH, post } from './api';
import styles from './RecordPage.module.css';

/**
 * Verify address (piece 13; decisions 0089, 0338). The Location page's panel
 * runs the seam on the stored address through `POST locations/:id/verify`,
 * and the Location form's step runs it on the typed address through
 * `POST locations/verify-address` before the record exists. Both draw a
 * Corrected answer beside what was typed with Accept and Keep as typed;
 * Accept on the page writes the record at once, and Accept in the form
 * rewrites the typed fields with the choice landing once the record saves.
 */
const FIELD_KEYS: Record<keyof Address, 'address.line1' | 'address.locality' | 'address.municipality' | 'address.administrativeArea' | 'address.county' | 'address.postalCode' | 'address.countryCode' | 'address.dependentLocality'> = {
  addressLines: 'address.line1',
  locality: 'address.locality',
  // Never offered as a correction; named here so the label map covers every address field (decisions 0354 and 0406).
  municipality: 'address.municipality',
  administrativeArea: 'address.administrativeArea',
  county: 'address.county',
  postalCode: 'address.postalCode',
  countryCode: 'address.countryCode',
  dependentLocality: 'address.dependentLocality',
};

/** Kept for the Settings page that imports it from here (T3206 owns that file); the builder itself lives in `@bms2/shared`. */
export { verifyLocationPath };
export const VERIFY_ADDRESS_PATH = `${PAGES_PATH}/locations/verify-address`;

/**
 * 🔒 What Edit Location opens with when Use This Address is pressed on a note (decision 7460): the address the service placed over the
 * lines, city, state and postal code, with every further line the Location holds kept as it stands (decision 7400). The county, the
 * building name and the phone are not the service's to move.
 */
export function placedPrefill(placed: Address, held: readonly string[]): Record<string, unknown> {
  const lines = placed.addressLines.length >= held.length ? [...placed.addressLines] : [...placed.addressLines, ...held.slice(placed.addressLines.length)];
  return { addressLines: lines, locality: placed.locality ?? '', administrativeArea: placed.administrativeArea ?? '', postalCode: placed.postalCode ?? '' };
}

async function ask(path: string, body: unknown, apiBaseUrl: string): Promise<LocationVerifyAnswer> {
  const answer = await post<LocationVerifyAnswer>(path, body, apiBaseUrl);
  if (!answer.ok) {
    // 🔒 The hour's hold answers its end as an instant, which the outcome reads in the viewer's clock (decision 2201).
    const heldUntil = (answer.body as { heldUntil?: unknown } | undefined)?.heldUntil;
    if (answer.status === 429 && typeof heldUntil === 'string') return failedAnswer(answer.message, null, heldUntil);
    throw new Error(answer.message);
  }
  return answer.data;
}

/**
 * 🔒 The hold in the viewer's clock, the hour before it as the failed attempt (decision 2201): never the instant the
 * server answered. An instant that does not parse draws nothing rather than a broken sentence.
 */
export function heldSentence(heldUntil: string, zone: string): string | null {
  const until = new Date(heldUntil);
  if (Number.isNaN(until.getTime())) return null;
  const failedAt = new Date(until.getTime() - RETRY_AFTER_MS);
  return fill(t('address.heldUntil'), { time: clockInZone(until, zone), failedAt: clockInZone(failedAt, zone) });
}

/** The corrections as rows of a list, field by field: what was typed and what is offered. */
export function CorrectionsList({ corrections }: { corrections: readonly AddressCorrection[] }) {
  return (
    <ul className={styles.corrections} data-address-corrections={corrections.length}>
      <li className={styles.correctionHead} aria-hidden="true">
        <span>{t('address.correction.field')}</span>
        <span>{t('address.correction.typed')}</span>
        <span>{t('address.correction.offered')}</span>
      </li>
      {corrections.map((c) => (
        <li key={c.field} className={styles.correction} data-correction-field={c.field}>
          <span className={styles.correctionField}>{t(FIELD_KEYS[c.field])}</span>
          <span>{c.from ?? '—'}</span>
          <span>{c.to ?? '—'}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The mailing city and the municipality on one line (decision 0406). One
 * building, two names: the postal service delivers to Aliquippa and the
 * building stands in Hopewell Township, and no document prints the second.
 */
function MailingCityLine({ answer }: { answer: LocationVerifyAnswer }) {
  const city = answer.normalized?.locality ?? null;
  const municipality = answer.municipality ?? answer.normalized?.municipality ?? null;
  if (!municipality || !city) return null;
  const line = [`${t('address.mailingCity')} ${city}`, `${t('address.municipality')} ${municipality}`].join(' \u00b7 ');
  return (
    <span className={styles.municipality} data-address-municipality={municipality}>
      {line}
    </span>
  );
}

/** One service's name where several are named together; a key the page does not know reads as itself. */
function sourceName(source: string): string {
  return source in ADDRESS_SOURCE_NAME_KEYS ? t(ADDRESS_SOURCE_NAME_KEYS[source as keyof typeof ADDRESS_SOURCE_NAME_KEYS]) : source;
}

/** The order services are named in, the Postal Service's rank first; a key the page does not know comes last. */
const SOURCE_ORDER: readonly string[] = Object.keys(ADDRESS_SOURCE_NAME_KEYS);
const sourceRank = (source: string) => (SOURCE_ORDER.includes(source) ? SOURCE_ORDER.indexOf(source) : SOURCE_ORDER.length);

/** Several names in one phrase, in the services' own order: *USPS and Google*, *USPS, Google and the Census Bureau*. */
export function sourceNames(sources: readonly string[]): string {
  const names = [...sources].sort((a, b) => sourceRank(a) - sourceRank(b)).map(sourceName);
  const phrase = names.length <= 2 ? names.join(' and ') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return phrase.charAt(0).toUpperCase() + phrase.slice(1);
}

/**
 * 🔒 Which service offered an address (decisions 0983, 3120): one names
 * itself, and several that agree are named together. A key the page does not
 * know reads as itself rather than as nothing.
 */
export function sourceText(sources: readonly string[]): string {
  if (sources.length > 1) return fill(t('address.source.agree'), { names: sourceNames(sources) });
  const only = sources[0] ?? '';
  return only in ADDRESS_SOURCE_LABEL_KEYS ? t(ADDRESS_SOURCE_LABEL_KEYS[only as keyof typeof ADDRESS_SOURCE_LABEL_KEYS]) : only;
}

/**
 * 🔒 The verification line (decision 3121): how many of the adapters asked
 * agreed, and which. *Verified by 3 of 3: USPS, Google, the Census Bureau*;
 * *Needs review: 1 of 3 answered*. An answer with no agreement draws nothing.
 */
export function agreementText(agreement: AddressAgreement, outcome: LocationVerifyAnswer['outcome']): string | null {
  const asked = agreement.asked.length;
  if (asked === 0) return null;
  const names = agreement.agreeing.map((source, index) => (index === 0 ? sourceNames([source]) : sourceName(source))).join(', ');
  if (agreement.met) return fill(t(outcome === 'Verified' ? 'address.agreement.verified' : 'address.agreement.agreed'), { count: agreement.agreeing.length, asked, names });
  if (agreement.answered.length <= 1) return fill(t('address.agreement.review'), { answered: agreement.answered.length, asked });
  if (agreement.disagree) return fill(t('address.agreement.disagree'), { answered: agreement.answered.length, asked });
  return fill(t('address.agreement.short'), { count: agreement.agreeing.length, asked, needed: agreement.needed });
}

function AgreementLine({ answer }: { answer: LocationVerifyAnswer }) {
  const text = answer.agreement ? agreementText(answer.agreement, answer.outcome) : null;
  if (!text || !answer.agreement) return null;
  return (
    <span className={styles.agreement} data-address-agreement={answer.agreement.met ? 'met' : 'review'}>
      {text}
    </span>
  );
}

/** Every address on offer, each with its differing parts, the service that offered it and its own Use this address. */
function CandidateList({ candidates, choice, onUse }: { candidates: readonly AddressCandidate[]; choice: VerifyChoice | null; onUse: (index: number) => void }) {
  return (
    <ul className={styles.candidates} data-address-candidates={candidates.length}>
      {candidates.map((candidate, index) => (
        <li key={`${candidate.address.addressLines.join(' ')}-${candidate.address.postalCode ?? ''}`} className={styles.candidate} data-address-candidate={index}>
          <span className={styles.candidateAddress}>{postalAddressLine(candidate.address)}</span>
          {candidate.sources?.length ? <span className={styles.candidateSource} data-address-candidate-source={candidate.sources.join(' ')}>{sourceText(candidate.sources)}</span> : null}
          {candidate.corrections.length === 0 && candidate.sources?.length && !candidate.openQuestion ? <span className={styles.candidateSource} data-address-candidate-as-typed="true">{t('address.source.asTyped')}</span> : null}
          {/* An option a service holds exactly as typed has nothing to compare, so it draws no empty table (decision 0983). */}
          {candidate.corrections.length > 0 ? <CorrectionsList corrections={candidate.corrections} /> : null}
          {/* 🔒 The Postal Service confirmed the building and not the unit, which is the question it leaves open (decision 3120). */}
          {candidate.openQuestion === 'unit' ? <span className={styles.candidateSource} data-address-open-question="unit">{t('address.question.unit')}</span> : null}
          {choice === null ? <Button size={Size.Small} text={[t('address.suggestion.accept')]} actions={[() => onUse(index)]} /> : null}
        </li>
      ))}
    </ul>
  );
}

export interface OutcomeProps {
  answer: LocationVerifyAnswer;
  choice: VerifyChoice | null;
  /** True in the form, where the answer lands only once the record saves. */
  typed: boolean;
  onAccept: (candidateIndex: number) => void;
  onKeep: () => void;
  /** Asks again, which a busy map service's answer offers as Verify Again (T1874; decision 2710). */
  onRetry?: (() => void) | undefined;
  /** Use This Address on a note, which opens Edit Location filled with what the service placed (decision 7460); only the page's panel offers it. */
  onUsePlaced?: ((placed: Address) => void) | undefined;
}

/**
 * 🔒 Each service's low-confidence answer, one line apiece under the result (decision 7430, amending 7320): the service, how sure
 * it was and the address it placed, with the sentence on why and what to check beneath. It never changes the result above it.
 */
export function ConfidenceNotes({ answer, onUsePlaced }: { answer: LocationVerifyAnswer; onUsePlaced?: ((placed: Address) => void) | undefined }) {
  const notes = answer.notes ?? [];
  if (answer.outcome === 'Failed' || notes.length === 0) return null;
  return (
    <ul className={styles.addressNotes} data-address-notes={notes.length} aria-label={t('address.note.heading')}>
      {notes.map((note) => (
        <li key={note.service} className={styles.addressNote} data-address-note={note.service}>
          <span className={styles.addressNoteLine}>{noteLine(note)}</span>
          {/* 🔒 Use This Address opens Edit Location filled with the address the service placed, where it differs from the typed one (decision 7460). */}
          {onUsePlaced && note.placed ? (
            <span className={styles.addressNoteAct} data-address-note-use={note.service}>
              <Button size={Size.Small} color={Color.Inactive} text={[t('address.suggestion.accept')]} actions={[() => onUsePlaced(note.placed!)]} />
            </span>
          ) : null}
          <span className={styles.addressNoteSentence}>{noteSentence(note)}</span>
        </li>
      ))}
    </ul>
  );
}

/** One answer, drawn the same way on the page, in the form and in Set up a state's local step (decision 1832). */
export function Outcome(props: OutcomeProps) {
  return (
    <>
      <OutcomeBody {...props} />
      <ConfidenceNotes answer={props.answer} onUsePlaced={props.onUsePlaced} />
    </>
  );
}

function OutcomeBody({ answer, choice, typed, onAccept, onKeep, onRetry }: OutcomeProps) {
  // 🔒 The shell's zone, never the machine's (decision 0261), so the page, the form and Set Up a State read one clock.
  const zone = useViewerZone();
  if (answer.outcome === 'Failed' && answer.busy) {
    // 🔒 A busy map service holds nothing, so the answer offers Verify Again at once (T1874; decision 2710).
    return (
      <p className={styles.verifyFailed} role="status" data-address-busy="true">
        <span>{t('address.busy')}</span>
        {onRetry ? <Button size={Size.Small} text={[t('address.verifyAgain')]} actions={[onRetry]} /> : null}
      </p>
    );
  }
  if (answer.outcome === 'Failed') {
    // 🔨 The provider's own words stand on the System error row, never on the page (decisions 2200, 2201).
    const held = answer.heldUntil ? heldSentence(answer.heldUntil, zone) : null;
    return (
      <p className={styles.verifyFailed} role="status" data-address-failed="true">
        <span>{t('address.failed')}</span>
        {held ? <span data-address-held-until={answer.heldUntil}>{held}</span> : null}
      </p>
    );
  }
  if (answer.outcome === 'Corrected') {
    // A single candidate reads as it always did; several read as a pick list (decision 0406).
    // 🔨 A server that answers without the key still draws: the one suggestion is the fallback.
    const offered = answer.candidates ?? [];
    const candidates = offered.length > 0 ? offered : answer.normalized ? [{ address: answer.normalized, corrections: answer.corrections ?? [] }] : [];
    return (
      <div className={styles.suggestion} data-address-suggestion={choice ?? 'open'} role="group" aria-label={t('address.suggestion.title')}>
        <span className={styles.verifyTitle}>{t(candidates.length > 1 ? 'address.suggestion.pick' : 'address.suggestion.title')}</span>
        <AgreementLine answer={answer} />
        <CandidateList candidates={candidates} choice={choice} onUse={onAccept} />
        {choice === null ? (
          <span className={styles.actions}>
            <Button size={Size.Small} color={Color.Inactive} text={[t('address.suggestion.keep')]} actions={[onKeep]} />
          </span>
        ) : (
          <p className={styles.hint} data-address-choice={choice}>{t(choice === 'accept' ? (typed ? 'address.acceptedTyped' : 'address.accepted') : typed ? 'address.keptTyped' : 'address.kept')}</p>
        )}
      </div>
    );
  }
  if (answer.outcome === 'Estimated') {
    // 🔒 The street and the postal code are confirmed and the number is estimated (decision 7460): the point is unlocked, the tax waits for Verified by User.
    return (
      <p className={styles.hint} role="status" data-address-outcome="Estimated" data-address-evidence={answer.evidence}>
        <span>{t(typed ? 'address.estimatedTyped' : 'address.estimated')}</span>{' '}
        <span>{t('address.estimated.street')}</span>
        {answer.fromCache ? <span>{t('address.fromCache')}</span> : null}
      </p>
    );
  }
  if (answer.outcome === 'Unverifiable') {
    // 🔨 A refusal says what the map service did confirm, so a person knows
    // whether to check the house number or the whole address (decision 0406).
    const evidence = answer.evidence === 'street' ? 'address.unverifiable.street' : answer.evidence === 'postalArea' ? 'address.unverifiable.postalArea' : 'address.unverifiable.none';
    return (
      <p className={styles.hint} role="status" data-address-outcome="Unverifiable" data-address-evidence={answer.evidence}>
        <span>{t(typed ? 'address.unverifiableTyped' : 'address.unverifiable')}</span>
        <span>{t(evidence)}</span>
        {answer.reason ? <span data-address-reason="true">{answer.reason}</span> : null}
        {answer.fromCache ? <span>{t('address.fromCache')}</span> : null}
      </p>
    );
  }
  return (
    <p className={styles.hint} role="status" data-address-outcome={answer.outcome}>
      <span>{t(typed ? 'address.verifiedTyped' : 'address.verified')}</span>
      <AgreementLine answer={answer} />
      <MailingCityLine answer={answer} />
      {answer.fromCache ? <span>{t('address.fromCache')}</span> : null}
    </p>
  );
}

export interface LocationVerifyOptions {
  locationId: string;
  apiBaseUrl?: string;
  /** The page reloads its tiles, so the pill and the fields read what was written. */
  onChanged: () => void;
  onError: (message: string) => void;
}

/**
 * The page's Verify address state, held by the page rather than the tile: a
 * reload re-reads the record and remounts its tiles, and the answer and the
 * person's choice have to outlive that.
 */
export function useLocationVerify({ locationId, apiBaseUrl = '', onChanged, onError }: LocationVerifyOptions) {
  const verify = useCallback((_address: Address) => ask(verifyLocationPath(locationId), {}, apiBaseUrl), [apiBaseUrl, locationId]);
  const step = useVerifyStep({ verify });
  const answered = useRef<string | null>(null);
  const { answer } = step.state;

  // A Verified or an Unverifiable answer has already been written; the page re-reads once.
  useEffect(() => {
    if (!answer || answer.outcome === 'Corrected' || answer.outcome === 'Failed') return;
    const key = `${answer.outcome}:${answer.verificationStatus ?? ''}`;
    if (answered.current === key) return;
    answered.current = key;
    onChanged();
  }, [answer, onChanged]);

  const { accept, keep, run } = step;
  const decide = useCallback(
    async (choice: VerifyChoice, candidateIndex = 0) => {
      // Which of the offered addresses was picked rides with the choice (decision 0406).
      const outcome = await post<LocationVerifyAnswer>(verifyLocationPath(locationId), { choice, candidateIndex }, apiBaseUrl);
      if (!outcome.ok) {
        onError(outcome.message);
        return;
      }
      if (choice === 'accept') accept(candidateIndex);
      else keep();
      onChanged();
    },
    [accept, apiBaseUrl, keep, locationId, onChanged, onError],
  );
  const start = useCallback(() => void run({ countryCode: '', addressLines: [] }), [run]);
  /**
   * 🔒 Verified by User (decision 7460): the Estimated answer on the panel is spent once the person has confirmed the number, so it
   * is cleared and the page re-reads the stored status.
   */
  const reset = step.reset;
  const verifyByHand = useCallback(async () => {
    const written = await post(verifiedByUserPath(locationId), {}, apiBaseUrl);
    if (!written.ok) {
      onError(written.message);
      return;
    }
    reset();
    answered.current = null;
    onChanged();
  }, [apiBaseUrl, locationId, onChanged, onError, reset]);
  return { state: step.state, start, decide, verifyByHand };
}

export interface LocationVerifyPanelProps {
  locationId: string;
  verify: ReturnType<typeof useLocationVerify>;
  /** The stored status; NotApplicable is a place with no address, which draws no Verify address (decision 0673). */
  verificationStatus?: string | null;
  /** Whether the viewer may write the Location, which Verified by User asks for (decision 7460). */
  editable?: boolean;
  /** A Verified or Estimated address the page holds no coordinates for, which the panel says rather than leave blank (decision 7560). */
  coordinatesMissing?: boolean;
  /** Use This Address on a note: the page opens Edit Location filled with what the service placed (decision 7460). */
  onUsePlaced?: ((placed: Address) => void) | undefined;
}

/** The General Information tile's Verify address on a Location page. */
export function LocationVerifyPanel({ locationId, verify, verificationStatus = null, editable = false, coordinatesMissing = false, onUsePlaced }: LocationVerifyPanelProps) {
  const { answer, choice, pending } = verify.state;
  // 🔒 A place named rather than addressed has nothing to verify, so it says so in place of the button (decision 0673).
  if (verificationStatus === 'NotApplicable') {
    return (
      <div className={styles.verify} data-location-verify={locationId} data-location-unaddressed="true">
        <p className={styles.hint}>{t('address.notApplicable')}</p>
      </div>
    );
  }
  return (
    <div className={styles.verify} data-location-verify={locationId}>
      <div className={styles.actions}>
        <Button size={Size.Small} text={[pending ? t('address.verifying') : t('address.verify')]} actions={[verify.start]} disabled={pending} />
        {/* 🔒 Verified by User sits beside Verify Address while the stored status is Estimated, for a person who may write the Location (decision 7460). */}
        {verificationStatus === 'Estimated' && editable ? (
          <span data-location-verified-by-user={locationId}>
            <Button size={Size.Small} color={Color.Inactive} text={[t('address.verifiedByUser')]} actions={[() => void verify.verifyByHand()]} disabled={pending} />
          </span>
        ) : null}
      </div>
      {/* 🔒 Verified or Estimated with no point: the sentence stands until the coordinates are taken, with Verify Address above it (decision 7560; T3117). */}
      {coordinatesMissing ? <p className={styles.hint} role="status" data-location-coordinates-missing="true">{t('address.coordinatesMissing')}</p> : null}
      {verificationStatus === 'Estimated' && editable && !answer ? <p className={styles.hint} data-location-verified-by-user-hint="true">{t('address.verifiedByUser.hint')}</p> : null}
      {answer ? <Outcome answer={answer} choice={choice} typed={false} onAccept={(index) => void verify.decide('accept', index)} onKeep={() => void verify.decide('keep')} onRetry={pending ? undefined : verify.start} onUsePlaced={onUsePlaced} /> : null}
    </div>
  );
}

export interface LocationVerifyStepProps {
  /** The address as the form holds it now. */
  address: Address;
  apiBaseUrl?: string;
  /** Accept rewrites the form's fields with the standardised address. */
  onAccept: (normalized: Address) => void;
  /** What the save should land on the record: the outcome and the choice, or null when nothing was verified. */
  onAnswered: (landing: { outcome: LocationVerifyAnswer['outcome']; choice: VerifyChoice | null } | null) => void;
}

/** The Location form's Verify step, over the typed address. */
export function LocationVerifyStep({ address, apiBaseUrl = '', onAccept, onAnswered }: LocationVerifyStepProps) {
  const verify = useCallback((typed: Address) => ask(VERIFY_ADDRESS_PATH, typed, apiBaseUrl), [apiBaseUrl]);
  const step = useVerifyStep({ verify });
  const { answer, choice } = step.state;
  const verifiedFor = useRef<string | null>(null);
  const key = JSON.stringify(address);

  // An address edited under an answer makes the answer stale, except the edit Accept itself made.
  const reset = step.reset;
  useEffect(() => {
    if (answer && verifiedFor.current !== null && verifiedFor.current !== key) {
      reset();
      verifiedFor.current = null;
    }
  }, [answer, key, reset]);

  useEffect(() => {
    onAnswered(answer && answer.outcome !== 'Failed' ? { outcome: answer.outcome, choice } : null);
  }, [answer, choice, onAnswered]);

  const run = () => {
    verifiedFor.current = key;
    void step.run(address);
  };
  const accept = (candidateIndex: number) => {
    const picked = step.accept(candidateIndex);
    if (!picked) return;
    verifiedFor.current = JSON.stringify(picked);
    onAccept(picked);
  };

  return (
    <div className={styles.verify} data-address-verify-step="true">
      <div className={styles.actions}>
        <Button size={Size.Small} color={Color.Inactive} text={[step.state.pending ? t('address.verifying') : t('address.verify')]} actions={[run]} disabled={step.state.pending || address.addressLines.every((line) => !line.trim())} />
        <span className={styles.hint}>{t('address.stepHint')}</span>
      </div>
      {answer ? <Outcome answer={answer} choice={choice} typed onAccept={accept} onKeep={step.keep} onRetry={step.state.pending ? undefined : run} /> : null}
    </div>
  );
}

/** The address the Location form holds, from its field values. */
export function addressFromFields(read: (key: string) => unknown): Address {
  const lines = read('addressLines');
  const text = (key: string) => {
    const value = read(key);
    return typeof value === 'string' && value.trim() ? value : null;
  };
  return {
    countryCode: text('countryCode') ?? 'US',
    addressLines: Array.isArray(lines) ? lines.map(String) : typeof lines === 'string' ? [lines] : [],
    locality: text('locality'),
    administrativeArea: text('administrativeArea'),
    postalCode: text('postalCode'),
  };
}
